/**
 * Un enfant dans la liste : sa photo, son âge, et l'état de sa fiche.
 *
 * La carte entière est cliquable — pas un lien « Voir la fiche » en petit. Au
 * pouce, une cible de la taille de la carte ne se manque pas ; un lien de
 * quarante pixels de large, si.
 *
 * Elle ne répète pas les semaines réservées : « Mes inscriptions », juste en
 * dessous, les groupe déjà par enfant et porte en plus leur statut. Deux fois
 * la même information, c'est deux fois plus à faire défiler sur un téléphone.
 */

import { Link } from 'react-router-dom';
import { ChevronRight, User } from 'lucide-react';
import { getAgeFromDate } from '../../utils/date';
import { resumeEnfant, type CompletudeEnfant } from '../../lib/completude';
import { PastilleFiche } from './Pastille';

export interface EnfantAffiche {
  id: string;
  prenom: string;
  nom: string;
  date_naissance: string;
  photo_signed_url?: string | null;
  photo_url?: string | null;
  completude: CompletudeEnfant;
}

const CarteEnfant = ({ enfant }: { enfant: EnfantAffiche }) => {
  const photo = enfant.photo_signed_url || enfant.photo_url;

  return (
    /*
      La destination depend de l'etat : une fiche a faire mene au parcours de
      verification, une fiche a jour a sa consultation. Envoyer les deux au
      meme endroit obligerait le parent a chercher lui-meme le bon bouton.
    */
    <Link
      to={enfant.completude.aFaire ? `/kids/${enfant.id}/verifier` : `/kids/${enfant.id}`}
      className="flex items-center gap-3 rounded-xl bg-white p-3 ring-1 ring-gray-200 transition-colors hover:bg-gray-50 sm:p-4"
    >
      <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-full bg-primary-100">
        {photo ? (
          <img src={photo} alt="" className="h-full w-full object-cover" />
        ) : (
          <span className="flex h-full w-full items-center justify-center font-semibold text-primary-700">
            {enfant.prenom?.[0]?.toUpperCase() ?? <User className="h-5 w-5" />}
          </span>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-gray-900">
          {enfant.prenom} {enfant.nom}
        </p>
        <p className="text-sm text-gray-500">{getAgeFromDate(enfant.date_naissance)} ans</p>

        <div className="mt-1.5">
          <PastilleFiche etat={enfant.completude.etat} resume={resumeEnfant(enfant.completude)} />
        </div>
      </div>

      <ChevronRight className="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
    </Link>
  );
};

export default CarteEnfant;
